"use client";

import React from "react";
import { ArrowRight, Phone, Shield, Star, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DenversMostTrustedShield() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const trustBadges = [{
    text: "Free Crawl Space Inspections"
  }, {
    text: "Local Des Moines Experts"
  }, {
    text: "Licensed & Insured"
  }, {
    text: "Financing Available"
  }];
  return <section data-section-id="156310" id="des-moines-crawl-space-experts" className="dark relative overflow-hidden bg-background pt-24 pb-16 lg:pt-32 lg:pb-24">
      {}
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-lg bg-secondary/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 h-[500px] w-[500px] rounded-lg bg-secondary/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={leftRef} className={`lg:col-span-7 flex flex-col gap-6 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="outline" className="w-fit border-secondary/20 bg-secondary/5 text-secondary gap-1.5 py-1 px-3 font-mono"><Shield className="size-3.5" aria-hidden="true" />Trusted by Des Moines homeowners.</Badge>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-foreground leading-[1.1] font-sans">
              Des Moines' Most 
              <span className="text-secondary relative whitespace-nowrap mx-2">
                Trusted
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-secondary/40" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" />
                </svg>
              </span> Crawl Space Experts
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {trustBadges.map((badge, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-foreground/90 font-medium">
                  <CheckCircle2 className="size-5 text-secondary shrink-0" />
                  <span className="text-sm sm:text-base">{badge.text}</span>
                </div>)}
            </div>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-medium">Don't let hidden moisture, toxic mold, or structural rot compromise your family's health. Schedule your free professional inspection today to protect your home's foundation and value.</p>
            
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <Button size="lg" className="h-14 px-8 text-base shadow-lg shadow-secondary/20 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-sm" asChild>
                <Link to="/ContactPage">Request Free Inspection<ArrowRight className="ml-2 size-5" aria-hidden="true" /></Link>
              </Button>
              
              <Button size="lg" variant="default" className="h-16 sm:h-14 px-10 text-xl sm:text-lg w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl rounded-sm transition-transform hover:scale-105 ring-2 ring-primary/20" asChild>
                <Link to="tel:5155859542"> Call Now: (515) 585-9542</Link>
              </Button>
            </div>

            {}
            <div className="flex items-center gap-8 mt-8 pt-8 border-t border-border/60">
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-foreground font-sans">5-Star</span>
                <span className="text-sm font-medium text-muted-foreground font-mono">Local Reviews</span>
              </div>
              <div className="w-px h-12 bg-border/60"></div>
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-foreground font-sans">100%</span>
                <span className="text-sm font-medium text-muted-foreground font-mono">Healthy Home</span>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 relative mt-10 lg:mt-0 transition-all duration-1000 delay-300 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:ml-auto">
              
              {}
              <div className="absolute -inset-4 bg-muted/50 -z-10 transition-transform duration-700 hover:scale-105" style={{
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 100%, 0% 100%, 0% 25%)'
            }} />
              
              {}
              <div className="relative w-full h-full bg-muted overflow-hidden shadow-2xl border border-border" style={{
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 100%, 0% 100%, 0% 25%)'
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/des_moines_skyline_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/des_moines_skyline_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/des_moines_skyline_hero.png-wvc-sizes" alt="Des Moines skyline at sunset" className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110" loading="lazy" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent pointer-events-none" />
              </div>

              {}
              <div className="absolute -bottom-6 -left-4 sm:-left-10 z-20 animate-[bounce_4s_infinite]">
                <div className="bg-card text-card-foreground border border-border shadow-xl rounded-sm p-4 flex flex-col gap-1 backdrop-blur-sm bg-card/95">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star, i) => <div key={star} data-index={i}>
                        <Star className="size-5 fill-secondary text-secondary drop-shadow-sm" />
                      </div>)}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-sm leading-tight font-sans">Top Rated</span>
                    <span className="text-xs text-muted-foreground font-mono">Des Moines Experts</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}